import { useState, useEffect } from "react";
import { X, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * "Continue on WhatsApp" — sends the latest match from the current web chat
 * session to the mechanic's WhatsApp number via Twilio. Bridges sessions so
 * future inbound WhatsApp messages resume the conversation.
 */
export default function HandoffDialog({ sessionId, onClose }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (phone.trim().length < 9) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/chat/handoff", { session_id: sessionId, phone });
      setDone(true);
      toast.success("Sent! Check your WhatsApp.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const joinHint =
    config?.whatsapp_join_code
      ? `If this is your first time, send "${config.whatsapp_join_code}" to ${config.whatsapp_number} from WhatsApp first to join the sandbox.`
      : null;

  return (
    <div
      data-testid="handoff-dialog"
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white border border-neutral-200 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono-tech text-[#FF5100]">
              CONTINUE ON WHATSAPP
            </div>
            <h2 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <MessageSquare size={20} className="text-[#25D366]" />
              Take this chat to WhatsApp
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              We'll send your latest match to your WhatsApp so you can carry on from there — even on the road.
            </p>
          </div>
          <button data-testid="handoff-close" onClick={onClose} className="text-neutral-500 hover:text-neutral-900">
            <X size={18} />
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">
                Your WhatsApp number
              </div>
              <input
                data-testid="handoff-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+27 82 123 4567"
                className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
              />
            </label>

            {joinHint && (
              <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 p-2">
                {joinHint}
              </div>
            )}

            <button
              type="submit"
              data-testid="handoff-submit"
              disabled={busy}
              className="w-full px-6 h-11 bg-[#25D366] hover:bg-[#1EBE5D] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : "Send to WhatsApp"}
            </button>
          </form>
        ) : (
          <div data-testid="handoff-done" className="border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            Sent ✓ Check your WhatsApp. Reply <strong>YES</strong> in WhatsApp for the supplier's contact details, or send a new part query — we'll remember where you left off.
          </div>
        )}
      </div>
    </div>
  );
}
